import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthUser } from '@runik/shared';
import { auth } from './client';
import { meQueryKey } from './hooks';

interface AuthContextValue {
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<AuthUser | null>;
}

const Ctx = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const value = useMemo<AuthContextValue>(
    () => ({
      login: async (email, password) => {
        const result = await auth.login(email, password);
        qc.setQueryData(meQueryKey, result.user);
        return result.user;
      },
      logout: async () => {
        await auth.logout();
        qc.clear();
      },
      refreshUser: async () => {
        const me = await auth.me();
        qc.setQueryData(meQueryKey, me);
        return me;
      },
    }),
    [qc],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within AuthProvider');
  return v;
}
